import { FormProvider, useForm } from "react-hook-form";
import { Grid, Typography } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { TRADE_SIDE, TRADE_TYPE } from "pages/BasicTrade/components/constants";
import { useParams, useSearchParams } from "react-router-dom";

import AdvanceTradeFormSkeleton from "components/Skeletons/AdvanceTradeForm.Skeleton";
import GoldTotal from "./GoldTotal";
import NumberFormats from "components/NumberFormats";
import { SpaceBetweenBox } from "components/GeneralStyled";
import SubmitTrade from "./SubmitTrade";
import TradeInput from "./TradeInput";
import TradeRange from "./TradeRange";
import TradeTotal from "./TradeTotal";
import { repeat } from "utils/helpers/interval";
import { useSelector } from "react-redux";
import tradeApi from "store/slices/trade/tradeApi";
import useNotify from "hooks/useNotify";

let getTradeAmountMaxInterval;

function AdvancedTradeInputs({ sideId, handleGetWalletInfo, setAdvancedTradeInputKey }) {
  const notify = useNotify()
  const { source, destination } = useParams();

  const [tradeAmountMax, setTradeAmountMax] = useState(null)

  const tradeInsertForm = useSelector((state) => state.trade.tradeInsertForm?.data);
  const coinMarketDetails = useSelector(
    (state) => state.market.investmentMarket?.data
  );

  const [searchParams] = useSearchParams();
  const amountTypeId = +searchParams.get("amountTypeId") || 1;
  const tradeTypeId = +searchParams.get("tradeTypeId") || 1;
  const basketId = +searchParams.get("basketId") || tradeInsertForm?.basketId || 0;
  const brokerId = +searchParams.get("brokerId") || tradeInsertForm?.brokerId || 0;
  const borrow = searchParams.get("borrow") || ""
  const repay = searchParams.get("repay") || ""


  const defaultValues = {
    basketId,
    brokerId,
    coinSourceId: +source,
    coinDestinationId: +destination,
    sideId,
    amountTypeId,
    tradeTypeId,
    amount: "",
    price: "",
  };

  const methods = useForm({ defaultValues });
  const { watch, reset } = methods;
  const coinSourceId = watch("coinSourceId");
  const coinDestinationId = watch("coinDestinationId");

  const isPrice = useMemo(() => {
    const selectedTradeType = tradeInsertForm?.lisTradeType?.find(
      (tradeType) => tradeType?.id === tradeTypeId
    );

    return selectedTradeType?.isPrice;
  }, [tradeInsertForm?.lisTradeType, tradeTypeId]);

  const coinPairSetting = useMemo(
    () =>
      tradeInsertForm?.lisCoinPairFeeRateTrade?.find(
        (setting) =>
          setting?.coinSourceId === coinSourceId &&
          setting?.coinDestinationId === coinDestinationId &&
          setting?.brokerId === brokerId
      ),
    [tradeInsertForm?.lisCoinPairFeeRateTrade, coinSourceId, coinDestinationId, brokerId]
  );

  const srcDecimalCount = coinPairSetting?.decimalPlacesCoinSource;
  const destDecimalCount = coinPairSetting?.decimalPlacesCoinDestination;

  const sourceCoin = useMemo(() => {
    return tradeInsertForm?.lisCoinPair?.find((coin) => coin?.coinSourceId === +source);
  }, [source, tradeInsertForm?.lisCoinPair]);

  const destinationCoin = useMemo(() => {
    return tradeInsertForm?.lisCoinPair?.find((coin) => coin?.coinDestinationId === +destination);
  }, [destination, tradeInsertForm?.lisCoinPair]);
  
  const handleGetTradeAmountMax = async () => {
    try {
      const { data } = await tradeApi.getTradeAmountMax({
        basketId,
        sideId,
        coinDestinationId: +destination,
        coinSourceId: +source,
        brokerId,
      })

      setTradeAmountMax(data?.data)
    } catch (error) {
      notify(
        error?.response?.data?.message ||
        error?.response?.message ||
        error?.message,
        "error"
      );

    }
  }

  useEffect(() => {
    if (tradeInsertForm?.validSeconds) {

      handleGetTradeAmountMax()

      clearInterval(getTradeAmountMaxInterval);

      getTradeAmountMaxInterval = repeat({
        action: () =>
          handleGetTradeAmountMax(),
        time: tradeInsertForm?.validSeconds * 1000,
      });
    }

    return () => {
      clearInterval(getTradeAmountMaxInterval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    tradeInsertForm?.validSeconds,
    destination,
    source,
    brokerId,
    tradeTypeId,
    basketId,
    sideId,
    borrow,
    repay
  ]);

  useEffect(() => {
    reset({
      coinSourceId: +source,
      coinDestinationId: +destination,
      sideId,
      amountTypeId,
      basketId,
      brokerId,
      tradeTypeId,
      amount: "",
      price: "",
    });
  }, [
    tradeTypeId,
    reset,
    amountTypeId,
    source,
    destination,
    sideId,
    basketId,
    brokerId,
  ]);

  return (
    <>
      {!false ? (
        <FormProvider {...methods}>
          <form>
            <Grid container>
              <Grid item xs={12}>
                <SpaceBetweenBox mb={0}>
                  <Typography color="text.secondary" variant="h6">
                    Available:
                  </Typography>

                  <Typography color="text.main" variant="h6">
                    <NumberFormats
                      value={tradeAmountMax?.availableBalance || 0}
                    />{" "}
                    <Typography
                      component="small"
                      variant="subtitle1"
                      color="text.secondary"
                    >
                      {sideId === TRADE_SIDE.Sell.id
                        ? sourceCoin?.coinSourceName
                        : destinationCoin?.coinDestinationName}
                    </Typography>
                  </Typography>
                </SpaceBetweenBox>
              </Grid>

              <TradeInput
                coinPairSetting={coinPairSetting}
                tradeAmountMax={tradeAmountMax}
                srcDecimalCount={srcDecimalCount}
                destDecimalCount={destDecimalCount}
                coinMarketDetails={coinMarketDetails}
                isPrice={isPrice}
              />

              <TradeRange
                tradeAmountMax={tradeAmountMax}
                srcDecimalCount={srcDecimalCount}
                destDecimalCount={destDecimalCount}
              />

              <GoldTotal
                tradeAmountMax={tradeAmountMax}
                sourceCoin={sourceCoin}
              />

              {tradeTypeId !== TRADE_TYPE.market.id && (
                <TradeTotal
                  tradeAmountMax={tradeAmountMax}
                  srcDecimalCount={srcDecimalCount}
                  destDecimalCount={destDecimalCount}
                />
              )}

              <SubmitTrade
                tradeAmountMax={tradeAmountMax}
                destinationCoin={destinationCoin}
                isPrice={isPrice}
                sideId={sideId}
                setAdvancedTradeInputKey={setAdvancedTradeInputKey}
                getTradeAmountMax={handleGetTradeAmountMax}
                handleGetWalletInfo={handleGetWalletInfo}
              />
            </Grid>
          </form>
        </FormProvider>
      ) : (
        <AdvanceTradeFormSkeleton />
      )}
    </>
  );
}

export default AdvancedTradeInputs;
